import { useCallback } from 'react';
import { useRealtimeSubscription } from '@/components/realtime/useRealtimeSubscription';
import { offlineDb } from '@/db/offlineDb';
import type { SyncableShoppingItem } from '@/db/offlineDb';
import { queryClient } from '@/services/queryClient';
import logger from '@/utils/logger';

/**
 * Converte un timestamp remoto (`updated_at`, stringa ISO o numero) in millisecondi.
 * Ritorna 0 quando il valore è assente o non parsabile: in quel caso un edit
 * locale `pending` vince sempre, che è la direzione sicura per offline-first.
 */
function parseRemoteTimestamp(raw: unknown): number {
  if (!raw) return 0;
  const n = typeof raw === 'string' ? new Date(raw).getTime() : Number(raw);
  return isNaN(n) ? 0 : n;
}

/**
 * Decide se un evento realtime INSERT/UPDATE deve scrivere il record remoto
 * nella offline DB, applicando la stessa politica pending-local-wins di
 * `BaseRepository.mergeData` (vedi issue #1419 / delta O4).
 *
 * - tombstone locale (`deleted`): NON reintrodurre il record remoto.
 * - locale `pending`: vince chi ha il timestamp più recente.
 * - altrimenti (`synced` o assente): il remoto vince.
 */
function shouldApplyRemoteUpsert(
  local: SyncableShoppingItem | undefined,
  remoteUpdatedAt: unknown
): boolean {
  if (!local) return true;
  if (local._syncStatus === 'deleted') return false;
  if (local._syncStatus === 'pending') {
    const localTs = local._lastModified ?? 0;
    return parseRemoteTimestamp(remoteUpdatedAt) > localTs;
  }
  return true;
}

/**
 * Subscribes to realtime changes on the shopping_items table.
 * Updates the offline DB and invalidates React Query caches.
 *
 * `activeListId` limita la subscription alla sola lista visualizzata: gli eventi
 * di altre liste non innescano più scritture Dexie + refetch a cascata (issue
 * #1422). Senza un id (es. nessuna lista primaria) non viene aperto alcun canale.
 */
export function useShoppingItemsSubscription(activeListId?: string | null) {
  const handleChange = useCallback(async (payload: any) => {
    const { eventType, new: newItem, old: oldItem } = payload;
    if (!offlineDb.isOpen()) {
      await offlineDb.open();
    }
    const listId = newItem?.list_id ?? oldItem?.list_id;
    try {
      if ((eventType === 'INSERT' || eventType === 'UPDATE') && newItem) {
        // pending-local-wins: non sovrascrivere un edit locale non ancora sincronizzato
        const local = await offlineDb.shoppingItems.get(newItem.id);
        if (shouldApplyRemoteUpsert(local, newItem.updated_at)) {
          await offlineDb.shoppingItems.put({
            ...newItem,
            attributes: newItem.attributes || [],
            _syncStatus: 'synced',
            _lastModified: Date.now()
          });
        }
      } else if (eventType === 'DELETE' && oldItem?.id) {
        // pending-local-wins: un edit locale pending non va cancellato da un
        // delete remoto; verrà risincronizzato. Un tombstone locale resta tale.
        const local = await offlineDb.shoppingItems.get(oldItem.id);
        if (!local || local._syncStatus === 'synced') {
          await offlineDb.shoppingItems.delete(oldItem.id);
        }
      }
      if (listId) {
        await queryClient.invalidateQueries({ queryKey: ['shopping-items', listId] });
        await queryClient.invalidateQueries({ queryKey: ['shopping-list', listId] });
        await queryClient.invalidateQueries({ queryKey: ['enhanced-primary-list'] });
      }
    } catch (err) {
      logger.error('useShoppingItemsSubscription', err);
    }
  }, []);

  const filter = activeListId ? `list_id=eq.${activeListId}` : undefined;
  useRealtimeSubscription('shopping_items', handleChange, {
    filter,
    enabled: !!activeListId
  });
}

export { shouldApplyRemoteUpsert, parseRemoteTimestamp };
